import AllInclusive from '@mui/icons-material/AllInclusive'
import { CircularProgress, Grid } from '@mui/material'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import AddTimerIntervalsComponent from 'src/consumer/components/TilesPagePractice/components/GenericTimerSelection/components/TimerSettings/AddIntervals'
import { getIntervalDurationLimits } from 'src/consumer/components/TilesPagePractice/components/GenericTimerSelection/components/TimerSettings/AddTimerIntervalsUtils'
import BellTypeDropDown from 'src/consumer/components/TilesPagePractice/components/GenericTimerSelection/components/TimerSettings/BellTypeDropDown'
import TimerTypeSelection from 'src/consumer/components/TilesPagePractice/components/GenericTimerSelection/components/TimerTypeSelection/TimerTypeSelection'
import { CustomInput } from 'src/consumer/components/helperComponents'
import { PRACTICE_TIMER_TYPES } from 'src/consumer/constants'
import { getDayJsDateFromMilliseconds } from 'src/shared/utils/dateTimeFormatting'
import { PracticeDoneButton } from '../../StreakPage/styledComponent'
import { AddPracticeSearchInputContainer } from '../AttachPracticeToExisting/styledComponents'
import { CreateNewPracticeStyledInput, MuiAddPracticeTimerTimeClockInput } from './styledComponents'
import { createNewPracticeTimer } from './utils'

export default function CreateNewPractice({
  title,
  duration,
  maxDuration,
  bellType,
  intervals,
  timerType,
  reminderTime,
  setTitle,
  setDuration,
  setMaxDuration,
  setBellType,
  setTimerType,
  setIntervals,
  setReminderTime,
  backToPreviousPage,
  clientSessionId,
  timerId,
}) {
  const [saving, setSaving] = useState(false)

  const intervalDurationLimits = useMemo(() => {
    return getIntervalDurationLimits(timerType, duration, maxDuration)
  }, [duration, maxDuration, timerType])

  const buttonIsDisabled = useMemo(() => {
    if (!title) {
      return true
    }
    if (isNaN(duration) || duration <= 0) {
      return true
    }

    if (timerType === PRACTICE_TIMER_TYPES.range) {
      if (isNaN(maxDuration)) {
        return true
      } else {
        if (maxDuration <= duration) {
          return true
        }
      }
    }
    if (intervals?.some((interval) => interval > intervalDurationLimits?.max)) {
      return true
    }
    return false
  }, [duration, maxDuration, timerType, title, intervalDurationLimits, intervals])

  function addReminderTime() {
    setReminderTime((prev) => [...prev, dayjs()])
  }

  async function handleSubmit() {
    setSaving(true)

    const uniqueIntervals = [...new Set(intervals)]
    const resp = await createNewPracticeTimer({
      timerData: {
        title,
        duration,
        bellType,
        maxDuration,
        minDuration: duration,
        intervals: uniqueIntervals?.sort((a, b) => a - b),
        preferredTimes: reminderTime,
        durationType: timerType,
        id: timerId,
      },
      clientSessionId,
    })
    if (resp.status === 200) {
      await backToPreviousPage()
    } else {
      toast.error('Something went wrong! Please try again.')
    }
    setSaving(false)
  }

  function convertDayJsDateIntoMilliSeconds(originalMilliseconds, dayJsDate) {
    if (!dayJsDate?.isValid()) {
      return NaN
    }

    return dayJsDate.minute() * 60 * 1000 + dayJsDate.second() * 1000
  }

  return (
    <>
      <div style={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              value={title}
              invalid={!title}
              component={CreateNewPracticeStyledInput}
              onChange={(e) => setTitle(e.target.value)}
              inputAndMessageContainer={AddPracticeSearchInputContainer}
              placeholder="Enter Title..."
              conponentContainerStyles={{ position: 'sticky', top: '0px', zIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
            <TimerTypeSelection
              timerType={timerType}
              setTimerType={(val) => setTimerType(val)}
              containerStyles={{ margin: '0px 30px', maxWidth: 'unset' }}
            />
          </Grid>
          {timerType !== PRACTICE_TIMER_TYPES.unlimited ? (
            <>
              <Grid
                item
                xs={timerType === PRACTICE_TIMER_TYPES.range ? 6 : 12}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gap="5px"
              >
                <span>Duration</span>
                <MuiAddPracticeTimerTimeClockInput
                  value={getDayJsDateFromMilliseconds(duration)}
                  format="mm:ss"
                  isTimeInvalid={isNaN(duration) || duration <= 0}
                  onChange={(val) =>
                    setDuration((prev) => convertDayJsDateIntoMilliSeconds(prev, val))
                  }
                />
              </Grid>
              {timerType === PRACTICE_TIMER_TYPES.range && (
                <Grid
                  item
                  xs={timerType === PRACTICE_TIMER_TYPES.range ? 6 : 12}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap="5px"
                >
                  <span>Max Duration</span>
                  <MuiAddPracticeTimerTimeClockInput
                    value={getDayJsDateFromMilliseconds(maxDuration)}
                    isTimeInvalid={isNaN(maxDuration) || maxDuration <= duration}
                    format="mm:ss"
                    onChange={(val) =>
                      setMaxDuration((prev) => convertDayJsDateIntoMilliSeconds(prev, val))
                    }
                  />
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
              <AllInclusive sx={{ fontSize: '60px' }} />
            </Grid>
          )}
          <BellTypeDropDown bellType={bellType} handleChange={(e) => setBellType(e.target.value)} />

          <AddTimerIntervalsComponent
            intervalDurationLimits={intervalDurationLimits}
            intervals={intervals}
            setIntervals={setIntervals}
          />
          {/* <AddPreferredTimes preferredTimes={reminderTime} setPreferredTimes={setReminderTime} /> */}
        </Grid>
      </div>

      <PracticeDoneButton
        disabled={buttonIsDisabled || saving}
        onClick={handleSubmit}
        sx={{ position: 'sticky', bottom: '20px', margin: '20px auto 0px' }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {saving && (
            <CircularProgress
              sx={{
                color: '#fff',
                position: 'absolute',
                width: '25px !important',
                height: '25px !important',
              }}
            />
          )}
          Done
        </div>
      </PracticeDoneButton>
    </>
  )
}
